import {
  Box,
  Button,
  Column,
  FormField,
  InputPasswordField,
  InputTextField,
  Row,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { AcceptTerms } from '~/components/authentication/components/AcceptTerms';
import { ReferralAccountExistsDialog } from '~/components/ReferralAccountExistsDialog';
import { OfficeTnCTypeEnum } from '~/domain/Consents';
import {
  gateSignUpOnReferralCode,
  normalizeReferralCode,
  readPendingReferralCode,
  referralCodeFromInput,
  setRecordedReferral,
} from '~/domain/Referral';
import { isReferralBlockedByExistingAccount, useSignUp } from '~/domain/SignUp';
import { useIsReferralRebuildEnabled } from '~/hooks/useIsReferralRebuildEnabled';
import { fireReferralLinkedFailed, fireReferralLinkedSuccess } from '~/posthogs/events/Referrals';
import { fireSignupClicked } from '~/posthogs/events/Signup';
import { routes } from '~/routes';
import { removeWhitespace } from '~/utils/String';
import {
  activateOnSpaceOrEnter,
  AuthErrorBanner,
  EMAIL_REGEX,
  LINK_STYLE,
  useAuthFormSubmit,
  useCancelPartialSession,
} from '../shared';
import { FreeToJoinBanner } from './FreeToJoinBanner';
import { REFERRAL_LINKED_STATE_KEY } from './shared/ReferralLinkedToast';

const MIN_PASSWORD_LENGTH = 8;

export interface ISignUpAccountFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  /** The v2 referral code (GOTU-7317). Present in the form only on the rebuild path. */
  referralCode: string;
  termsAccepted: boolean;
}

export const SignUpForm = () => {
  // GOTU-7435: the flag hook is subscribed and can change mid-flow during a
  // rollout. The value is captured once, when this form mounts, and threaded
  // through the request so a flip cannot switch the referral path of a
  // submission already in progress.
  const liveReferralRebuild = useIsReferralRebuildEnabled();
  const [isReferralRebuildEnabled] = useState(liveReferralRebuild);

  const methods = useForm<ISignUpAccountFormValues>({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      // Synchronous read, so a code captured from a referral link is already
      // in the field on first paint rather than appearing mid-typing.
      referralCode: isReferralRebuildEnabled ? (readPendingReferralCode() ?? '') : '',
      termsAccepted: false,
    },
    mode: 'onSubmit',
  });

  return (
    <FormProvider {...methods}>
      <SignUpFormInner isReferralRebuildEnabled={isReferralRebuildEnabled} />
    </FormProvider>
  );
};

// Order for picking which specific validation message to surface in the banner.
const FIELD_ORDER: Array<keyof ISignUpAccountFormValues> = ['email', 'password', 'confirmPassword'];

interface ISignUpFormInnerProps {
  /** The rebuild flag as captured at mount — see `SignUpForm`. */
  isReferralRebuildEnabled: boolean;
}

const SignUpFormInner = ({ isReferralRebuildEnabled }: ISignUpFormInnerProps) => {
  const navigate = useNavigate();
  const { mutate: signUp, isPending } = useSignUp();
  const cancelPartialSession = useCancelPartialSession();
  // True while the referral code lookup is in flight, so the button shows
  // loading between the click and either the sign-up call or the inline error.
  const [isVerifyingReferralCode, setIsVerifyingReferralCode] = useState(false);
  // GOTU-7434: the email already has an account, and a referral rode the
  // attempt. The generic failure modal names only the email, so this dialog
  // replaces it and tells the office the referral cannot apply.
  const [isAccountExistsOpen, setIsAccountExistsOpen] = useState(false);

  const {
    register,
    control,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<ISignUpAccountFormValues>();

  const { submit, bannerVisible, bannerRef } = useAuthFormSubmit<ISignUpAccountFormValues>({
    onValid: async ({ email, password, referralCode }) => {
      fireSignupClicked();
      // With the captured flag on, whatever is in the code field is sent —
      // autofilled or typed, there is no locked-vs-manual split (GOTU-7317).
      // Normalised once, in the domain layer, so a pasted ` uaj4-dj ` and a
      // link-resolved `UAJ4DJ` submit the same value; blank omits the key
      // rather than sending ''. With it off, no referral is sent at all.
      const referredByCode = isReferralRebuildEnabled
        ? normalizeReferralCode(referralCode) || undefined
        : undefined;
      // A code of the wrong shape, or one the server answers `no_match` for,
      // blocks sign-up with an inline error under the field. Blank skips the
      // gate, and so does a lookup outage — see `gateSignUpOnReferralCode`,
      // which also records a hand-typed code's claim on the way past.
      /**
       * The code this submit may confirm, once the sign-up comes back. Set only
       * on outcome `ok`, so it carries both facts the confirmation needs —
       * whether to confirm at all, and what to stamp on the event.
       * `unavailable` and `server_error` leave it undefined: they allow sign-up
       * but confirmed nothing, and saying "linked" when the lookup never
       * answered is a guess.
       */
      let confirmedReferralCode: string | undefined;

      if (referredByCode) {
        setIsVerifyingReferralCode(true);
        const gate = await gateSignUpOnReferralCode(referredByCode);
        setIsVerifyingReferralCode(false);
        if (!gate.allowed) {
          setError('referralCode', { type: 'lookup', message: gate.message });
          return;
        }
        if (gate.outcome === 'ok') confirmedReferralCode = referredByCode;
      }
      signUp(
        { email, password, referredByCode, isReferralRebuildEnabled },
        {
          /**
           * Confirmed on the sign-up response, never at gate time. The gate
           * resolves the code while the account still may not be created — an
           * already-registered email is the common case — and confirming
           * earlier put "Referral linked successfully" on screen next to
           * `ReferralAccountExistsDialog`, with a success event to match.
           *
           * The confirmation rides the navigation rather than rendering here:
           * this page unmounts on the hop. See `ReferralLinkedToast`.
           */
          onSuccess: () => {
            if (confirmedReferralCode) {
              setRecordedReferral({ email, code: confirmedReferralCode });
              fireReferralLinkedSuccess({ referral_code: confirmedReferralCode });
            }
            navigate(routes.signUpRedesign.yourInfo, {
              state: confirmedReferralCode ? { [REFERRAL_LINKED_STATE_KEY]: true } : undefined,
            });
          },
          // Every other failure keeps the generic path, which `useSignUp`
          // dispatches; it stays silent only for this one.
          onError: (error) => {
            if (!isReferralBlockedByExistingAccount(error, { referredByCode })) return;
            // Lost attribution, and the only failure reason the gate cannot
            // emit: the code resolved, so nothing failed until the account did.
            // Guarded on the *confirmed* code rather than the submitted one, so
            // a submit reports at most one linked_ event — a code that came
            // back `unavailable` already reported that reason.
            if (confirmedReferralCode) {
              fireReferralLinkedFailed({
                reason: 'account_exists',
                referral_code: confirmedReferralCode,
              });
            }
            setIsAccountExistsOpen(true);
          },
        },
      );
    },
  });

  const goToLogin = cancelPartialSession;

  const emailRegister = register('email', {
    required: 'Email is required',
    pattern: { value: EMAIL_REGEX, message: 'The email address entered is invalid.' },
  });

  // Surface every validation message (one per field) so the banner can stack
  // them. Required-empty errors stay suppressed — the empty-fields default
  // copy covers those.
  const specificErrorMessages = FIELD_ORDER.map((key) => errors[key])
    .filter((err): err is NonNullable<typeof err> => Boolean(err) && err?.type !== 'required')
    .map((err) => err.message as string | undefined)
    .filter((msg): msg is string => Boolean(msg));

  // When the user fails the "passwords match" check the error lives on
  // `confirmPassword`; the spec calls for highlighting both fields.
  const passwordMismatch = errors.confirmPassword?.type === 'validate';

  return (
    <form onSubmit={submit} data-testid="signup-form" noValidate>
      {/* Closing leaves the office on the form with everything still typed, so
          they can correct the email — the copy points them at logging in. */}
      <ReferralAccountExistsDialog
        open={isAccountExistsOpen}
        onClose={() => setIsAccountExistsOpen(false)}
      />
      <Column pt={6} gap={8}>
        <Column gap={4}>
          <Typography bold size="heading_3" color={colors.primary[500]}>
            Welcome, Offices!
          </Typography>
          <Typography variant="body1" color={colors.neutral[500]}>
            Create your account and join thousands of offices using GoTu to find trusted dental
            professionals quickly.
          </Typography>
        </Column>

        <FreeToJoinBanner />

        {/* Stable scrollIntoView target when the banner is hidden. */}
        <Box ref={bannerRef}>
          <AuthErrorBanner visible={bannerVisible} message={specificErrorMessages} />
          <Column gap={8} pt={bannerVisible ? 3 : 0}>
            <FormField label="Email">
              <InputTextField
                data-cy="input-email"
                placeholder="Enter your email"
                isError={Boolean(errors.email)}
                {...emailRegister}
                onChange={(e) => {
                  const stripped = removeWhitespace(e.target.value);
                  if (stripped !== e.target.value) e.target.value = stripped;
                  emailRegister.onChange(e);
                }}
              />
            </FormField>
            <FormField label="Password">
              <InputPasswordField
                data-cy="input-password"
                placeholder="Enter your password"
                isError={Boolean(errors.password) || passwordMismatch}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: MIN_PASSWORD_LENGTH,
                    message: `All passwords must be at least ${MIN_PASSWORD_LENGTH} characters.`,
                  },
                })}
              />
            </FormField>
            <FormField label="Confirm Password">
              <InputPasswordField
                data-cy="input-confirm-password"
                placeholder="Confirm your password"
                isError={Boolean(errors.confirmPassword)}
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) =>
                    value === getValues('password') || 'Passwords do not match. Please try again.',
                })}
              />
            </FormField>
            {/*
              GOTU-7317. Optional and deliberately ruleless — no `rules` that
              could block submit; the only error is the lookup gate's, set by
              hand on submit and cleared on the next edit. Autofilled from a
              referral link when there is one, typed by hand otherwise, and
              editable either way. A pasted referral link collapses to its code
              as it lands. Capitalisation and spell-check are off so a browser
              never mangles a six-character code; case and whitespace are
              normalised once, on submit.
            */}
            {isReferralRebuildEnabled ? (
              <Controller
                name="referralCode"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label="Referral Code" optional htmlFor="input-referral-code">
                    <InputTextField
                      id="input-referral-code"
                      data-cy="input-referral-code"
                      placeholder="Enter your referral code"
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={(e) => {
                        if (fieldState.error) clearErrors('referralCode');
                        field.onChange(referralCodeFromInput(e.target.value));
                      }}
                      isError={Boolean(fieldState.error)}
                      autoCapitalize="none"
                      autoComplete="off"
                      spellCheck={false}
                      aria-invalid={Boolean(fieldState.error)}
                      aria-describedby={fieldState.error ? 'referral-code-error' : undefined}
                    />
                    {fieldState.error?.message ? (
                      <Typography
                        id="referral-code-error"
                        data-testid="referral-code-error"
                        role="alert"
                        variant="body1"
                        color={colors.error[500]}
                      >
                        {fieldState.error.message}
                      </Typography>
                    ) : null}
                  </FormField>
                )}
              />
            ) : null}
          </Column>
        </Box>

        {/* Reuses the legacy AcceptTerms control so the new signup gates the
            toggle through the same scroll-to-accept consent popup as the
            pre-redesign flow — without it, BE flips the user back into the
            "updated terms" prompt on dashboard after they finish signup. */}
        <Box>
          <Controller
            name="termsAccepted"
            control={control}
            rules={{ validate: (v) => v === true || 'You must accept the Terms and Conditions' }}
            render={({ field }) => (
              <AcceptTerms
                value={field.value}
                onChange={field.onChange}
                consent={OfficeTnCTypeEnum.OFFICE_TNC}
              />
            )}
          />
        </Box>

        <Row justify="center">
          <Button
            data-cy="btn-sign-up"
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isPending || isVerifyingReferralCode}
          >
            Start Finding Coverage
          </Button>
        </Row>

        <Row justify="center" align="center" wrap="nowrap" gap={1}>
          <Typography variant="body1" color={colors.neutral[500]}>
            Already have an account?
          </Typography>
          <Typography
            as="span"
            bold
            variant="body1"
            color={colors.secondary[500]}
            style={LINK_STYLE}
            role="button"
            tabIndex={0}
            data-cy="link-log-in"
            onClick={goToLogin}
            onKeyDown={activateOnSpaceOrEnter(goToLogin)}
          >
            Log In
          </Typography>
        </Row>
      </Column>
    </form>
  );
};
